import React, { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import FeatureSection from "./components/FeatureSection";
import RulesPage from "./components/RulesPage";
import CommunityPage from "./components/CommunityPage";
import TopPlayersPage from "./components/TopPlayersPage";
import PlayerCostsPage from "./components/PlayerCostsPage";
import PlayerProfilePage from "./components/PlayerProfilePage";
import DownloadPage from "./components/DownloadPage";
import { Toaster } from "./components/ui/sonner";
import { ChevronDown, Globe, Menu, X } from "lucide-react";

// Import Assets
const LOGO_SMALL = "/assets/logo_small.webp";
const MONOGRAM = "/assets/monogram_v3.webp";

// Screenshots
const SCREEN_MATCH = "https://customer-assets.emergentagent.com/job_1befbfdb-180f-4e8b-97c4-b68190294449/artifacts/pgm7g8vi_Screenshot%202026-01-16%20143021.png"; 
const SCREEN_LEADERBOARD = "https://customer-assets.emergentagent.com/job_1befbfdb-180f-4e8b-97c4-b68190294449/artifacts/7szawlu6_Screenshot%202026-01-16%20143029.png"; 
const SCREEN_TEAMS = "https://customer-assets.emergentagent.com/job_1befbfdb-180f-4e8b-97c4-b68190294449/artifacts/nm7w0n9m_Screenshot%202026-01-16%20143048.png"; 
const SCREEN_DETAILS = "https://customer-assets.emergentagent.com/job_1befbfdb-180f-4e8b-97c4-b68190294449/artifacts/ymr07pd8_Screenshot%202026-01-16%20143057.png"; 

// Translations Dictionary
const content = {
    it: {
        nav: {
            join: "Unisciti ora",
            comingSoon: "Coming Soon",
            signIn: "Accedi",
            register: "Registrati",
            menu: {
                rules: "Regolamento",
                community: "Community",
                topPlayers: "Top Players",
                playerValues: "Prezzi Giocatori",
                download: "Scarica l'App",
                contact: "Contatti"
            }
        },
        hero: {
            titlePart1: "EVERY DAY",
            titlePart2: "FOOTBALL PLAY",
            subtitle: "Fanteex: Il futuro del fantasy football"
        },
        features: {
            section1: {
                title: "NON ASPETTARE IL WEEKEND",
                subtitle: "IL GIOCO",
                description: "Il calcio non si ferma mai. Perché dovrebbe farlo il tuo fantasy? Con Fanteex ogni partita è un'opportunità. Componi la tua rosa, investi i tuoi Teex e domina il mercato in tempo reale."
            },
            section2: {
                title: "SFIDA I TUOI AMICI",
                subtitle: "LA COMPETIZIONE",
                description: "Head-to-Head per la gloria immediata o Private Leagues per una guerra lunga una stagione. Scala le classifiche globali e dimostra di essere il vero esperto di Fanteex."
            }
        },
        rules: {
            title: "REGOLAMENTO",
            subtitle: "LE REGOLE DEL GIOCO",
            description: "Ogni utente parte con 500 TEEX. Crea la tua rosa spendendo fino a 10 TEEX per sfida. Sfida Head 2 Head o Private League fino a 10 partecipanti. I punti derivano da eventi reali: gol, assist, minuti giocati e molto altro.",
            cta: "Scopri tutte le regole"
        },
        community: {
            title: "LA COMMUNITY",
            subtitle: "VOCI DAL CAMPO",
            description: "Cinque persone, cinque modi diversi di vivere il fantasy. Scopri cosa dice chi gioca gia a Fanteex.",
            cta: "Scopri la community"
        },
        topPlayers: {
            title: "TOP PLAYERS",
            subtitle: "I MIGLIORI IN CAMPO",
            description: "Scopri i 5 giocatori più performanti di questa stagione. Chi sta dominando la Fanteex 2026/27?",
            cta: "Vedi tutti i top players",
            noPlayers: "Torna al prossimo Matchday per vedere i top players!"
        },
        playerCosts: {
            title: "PREZZI GIOCATORI",
            subtitle: "QUANTO COSTANO",
            description: "Controlla i prezzi aggiornati dei 5 giocatori più costosi. Pianifica la tua strategia di mercato.",
            cta: "Vedi tutti i prezzi",
            noPlayers: "Torna al prossimo Matchday per vedere i prezzi!"
        },
        publisherContent: {
            title: "GUIDA COMPLETA A FANTEEX",
            subtitle: "FANTASY CALCIO OGNI GIORNO",
            paragraphs: [
                "Fanteex e un fantasy calcio in cui ogni giornata della stagione puo diventare una nuova occasione di gioco. A differenza dei giochi classici legati a un solo weekend, qui puoi costruire la rosa in modo dinamico, seguire le partite in tempo reale e aggiornare la tua strategia partita dopo partita.",
                "Ogni utente parte con 500 TEEX virtuali e utilizza un budget massimo di 10 TEEX per la singola sfida. Il punteggio nasce da eventi reali: minuti giocati, gol, assist, cartellini, porta inviolata e rendimento della squadra. Questo rende il gioco basato su conoscenza, analisi e capacita decisionale, non sul caso.",
                "La piattaforma include modalita diverse per profili differenti: Head 2 Head per confronti diretti rapidi e Private League per gruppi di amici che vogliono competere durante la stagione. Il sistema ranking permette di monitorare costanza e performance, mentre le pagine Top Players e Prezzi Giocatori aiutano a preparare le scelte in modo piu informato.",
                "Su Fanteex pubblichiamo regole chiare, metodi di calcolo trasparenti e aggiornamenti continui sulla struttura di gioco. L'obiettivo e offrire una esperienza sportiva affidabile, leggibile e utile anche per nuovi utenti che vogliono capire rapidamente come funziona un fantasy football moderno orientato ai dati."
            ],
            faqTitle: "DOMANDE FREQUENTI",
            faqs: [
                {
                    question: "Come si vince su Fanteex?",
                    answer: "Si vince costruendo una rosa efficiente entro il budget, scegliendo giocatori con buon rendimento atteso e seguendo il calendario per ridurre il rischio. La gestione del capitale TEEX nel medio periodo e decisiva."
                },
                {
                    question: "Fanteex e adatto a principianti?",
                    answer: "Si. Il regolamento e spiegato in modo progressivo, con esempi pratici e metriche leggibili. Chi inizia puo partire dalle sfide Head 2 Head e passare poi alle Private League."
                },
                {
                    question: "I dati sono aggiornati?",
                    answer: "Le classifiche e i prezzi vengono aggiornati in base ai dati disponibili del matchday corrente. In caso di match posticipati, vengono applicate regole dedicate per mantenere l'equita competitiva."
                }
            ]
        },
        cta: {
            title: "VUOI GIOCARE, SAPERNE DI PIÙ O COLLABORARE CON FANTEEX?",
            description: "Se vuoi iniziare a giocare, ricevere informazioni o proporre una collaborazione, scrivici qui sotto. Ti risponderemo direttamente.",
            form: {
                labels: {
                    email: "Email",
                    message: "Messaggio"
                },
                placeholders: {
                    email: "La tua email",
                    message: "Raccontaci cosa ti interessa: giocare, info, partnership, altro…"
                },
                validationMessages: {
                    emailRequired: "Email obbligatoria",
                    emailInvalid: "Email non valida",
                    messageRequired: "Messaggio obbligatorio",
                    messageMin: "Scrivi almeno 10 caratteri"
                },
                buttonText: "SCRIVICI",
                submittingText: "Invio…",
                successMessage: "Messaggio inviato. Ti rispondiamo appena possibile.",
                errorMessage: "Errore nell’invio. Riprova tra poco."
            }
        },
        footer: {
            rights: "Tutti i diritti riservati."
        }
    },
    en: {
        nav: {
            join: "Join Now",
            comingSoon: "Coming Soon",
            signIn: "Sign In",
            register: "Register",
            menu: {
                rules: "Game Rules",
                community: "Community",
                topPlayers: "Top Players",
                playerValues: "Player Values",
                download: "Download App",
                contact: "Get in Touch"
            }
        },
        hero: {
            titlePart1: "EVERY DAY",
            titlePart2: "FOOTBALL PLAY",
            subtitle: "Fanteex: The Future of Fantasy Football"
        },
        features: {
            section1: {
                title: "DON'T WAIT FOR THE WEEKEND",
                subtitle: "THE GAME",
                description: "Football never stops. Why should your fantasy? With Fanteex, every match is an opportunity. Build your squad, invest your Teex, and dominate the real-time market."
            },
            section2: {
                title: "CHALLENGE YOUR FRIENDS",
                subtitle: "THE COMPETITION",
                description: "Head-to-Head for immediate glory or Private Leagues for a season-long war. Climb global leaderboards and prove you're the true Fanteex expert."
            }
        },
        rules: {
            title: "GAME RULES",
            subtitle: "HOW TO PLAY",
            description: "Every user starts with 500 TEEX. Build your squad spending up to 10 TEEX per challenge. Head 2 Head or Private League with up to 10 participants. Points come from real events: goals, assists, minutes played and much more.",
            cta: "Discover all the rules"
        },
        community: {
            title: "THE COMMUNITY",
            subtitle: "VOICES FROM THE FIELD",
            description: "Five people, five different ways to experience fantasy football. Discover what players are saying about Fanteex.",
            cta: "Discover the community"
        },
        topPlayers: {
            title: "TOP PLAYERS",
            subtitle: "THE BEST ON THE FIELD",
            description: "Discover the top 5 performing players this season. Who's dominating Fanteex 2026/27?",
            cta: "See all top players",
            noPlayers: "Come back next Matchday to see top players!"
        },
        playerCosts: {
            title: "PLAYER COSTS",
            subtitle: "HOW MUCH THEY COST",
            description: "Check updated prices for the 5 most expensive players. Plan your market strategy.",
            cta: "See all prices",
            noPlayers: "Come back next Matchday to see prices!"
        },
        publisherContent: {
            title: "COMPLETE GUIDE TO FANTEEX",
            subtitle: "FANTASY FOOTBALL EVERY DAY",
            paragraphs: [
                "Fanteex is a fantasy football platform where every matchday of the season can become a new game opportunity. Unlike classic formats limited to one weekend flow, you can build your squad dynamically, follow real matches in real time, and refine your strategy throughout the season.",
                "Each user starts with 500 virtual TEEX and uses a maximum budget of 10 TEEX for each challenge. Scoring is based on real events: minutes played, goals, assists, cards, clean sheets, and team performance. This creates a skill-based environment driven by analysis and decision quality.",
                "The product includes multiple formats for different players: Head 2 Head for direct fast competition and Private Leagues for groups of friends. Ranking systems, top players, and player cost pages support smarter decisions by giving context before every matchday.",
                "On Fanteex we publish clear rules, transparent scoring logic, and continuous product updates. Our goal is to provide a reliable sports experience with useful information for both experienced managers and newcomers approaching modern data-driven fantasy games."
            ],
            faqTitle: "FREQUENTLY ASKED QUESTIONS",
            faqs: [
                {
                    question: "How do you win on Fanteex?",
                    answer: "Winning depends on efficient budget allocation, high-quality player selection, and consistent adaptation to fixture context. Managing TEEX over time is as important as single-match decisions."
                },
                {
                    question: "Is Fanteex suitable for beginners?",
                    answer: "Yes. Rules are explained with practical examples, and users can start with Head 2 Head challenges before moving to more competitive Private Leagues."
                },
                {
                    question: "Are rankings and prices updated?",
                    answer: "Rankings and prices are refreshed according to the active matchday data. If matches are postponed, dedicated rules are applied to keep competition fair."
                }
            ]
        },
        cta: {
            title: "WANT TO PLAY, LEARN MORE, OR COLLABORATE WITH FANTEEX?",
            description: "If you want to start playing, receive information, or propose a collaboration, write us below. We'll reply directly.",
            form: {
                labels: {
                    email: "Email",
                    message: "Message"
                },
                placeholders: {
                    email: "Your email",
                    message: "Tell us what you're interested in: playing, info, partnership, other…"
                },
                validationMessages: {
                    emailRequired: "Email is required",
                    emailInvalid: "Invalid email address",
                    messageRequired: "Message is required",
                    messageMin: "Please write at least 10 characters"
                },
                buttonText: "WRITE TO US",
                submittingText: "Sending…",
                successMessage: "Message sent. We'll reply as soon as possible.",
                errorMessage: "Sending failed. Please try again soon."
            }
        },
        footer: {
            rights: "All rights reserved."
        }
    }
};

// Complex Tech Background
const TechBackground = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        {/* Dynamic Gradient Mesh */}
        <div className="absolute top-[-20%] left-[-20%] w-[80vw] h-[80vw] bg-[#1a0b2e] rounded-full blur-[100px] opacity-80" />
        <div className="absolute bottom-[-20%] right-[-20%] w-[80vw] h-[80vw] bg-[#0f0518] rounded-full blur-[100px] opacity-90" />
        
        {/* The "Studied" Grid System */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(93,253,203,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(93,253,203,0.03)_1px,transparent_1px)] bg-[size:100px_100px] perspective-[1000px] transform rotateX(60deg) scale(1.5) origin-top" />

        <div className="hidden md:block">
          {/* Sophisticated Laser Beams */}
          <div className="absolute top-0 left-1/2 w-[1px] h-full bg-gradient-to-b from-transparent via-[#5DFDCB]/20 to-transparent blur-[1px] animate-[pulse_4s_infinite]" />

          {/* Geometric Intersections */}
          <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 120, repeat: Infinity, ease: "linear" }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100vh] h-[100vh] border border-[#5DFDCB]/5 rounded-full"
          >
               <div className="absolute top-0 left-1/2 w-[2px] h-4 bg-[#5DFDCB]/50" />
               <div className="absolute bottom-0 left-1/2 w-[2px] h-4 bg-[#5DFDCB]/50" />
               <div className="absolute left-0 top-1/2 w-4 h-[2px] bg-[#5DFDCB]/50" />
               <div className="absolute right-0 top-1/2 w-4 h-[2px] bg-[#5DFDCB]/50" />
          </motion.div>

           <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 180, repeat: Infinity, ease: "linear" }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vh] h-[60vh] border border-[#5DFDCB]/5 rounded-full border-dashed"
          />

          {/* Floating Particles */}
          {Array.from({ length: 10 }).map((_, i) => (
               <motion.div
                  key={i}
                  initial={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      opacity: 0
                  }}
                  animate={{
                      y: [0, (Math.random() - 0.5) * 140],
                      opacity: [0, 0.5, 0]
                  }}
                  transition={{
                      duration: Math.random() * 8 + 8,
                      repeat: Infinity,
                      ease: "linear",
                      delay: Math.random() * 4
                  }}
                  className="absolute w-1 h-1 bg-[#5DFDCB] rounded-full blur-[1px]"
              />
          ))}
        </div>
    </div>
);

const ScrollToHash = () => {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash) {
      return;
    }

    const targetId = hash.slice(1);
    const target = document.getElementById(targetId);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash, pathname]);

  return null;
};

// Top Players Preview Component
const TOP_PLAYERS_SEASON = "2026/27";
const TOP_PLAYERS_COMPETITION = "all";

const TopPlayersPreview = ({ lang, t }) => {
  const [players, setPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadTopPlayers = async () => {
      try {
        setIsLoading(true);
        const isLocalhost = window.location.hostname === "localhost";
        const base = isLocalhost ? "http://localhost:8000" : "https://app.fanteex.com";
        const apiUrl = `${base}/api/top-players?season=${encodeURIComponent(TOP_PLAYERS_SEASON)}&competition=${encodeURIComponent(TOP_PLAYERS_COMPETITION)}`;
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        const data = await response.json();
        const list = Array.isArray(data) ? data : (data?.data || []);
        if (isMounted) {
          setPlayers(list.slice(0, 5));
        }
      } catch (error) {
        if (isMounted) {
          setPlayers([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadTopPlayers();

    return () => {
      isMounted = false;
    };
  }, []);

  const roleInitials = {
    GK: { it: "P", en: "G" },
    DEF: { it: "D", en: "D" },
    MID: { it: "C", en: "M" },
    ATT: { it: "A", en: "A" }
  };

  const normalizePosition = (value) => {
    if (!value) return "";
    const raw = String(value).trim().toUpperCase();
    const map = {
      G: "GK", D: "DEF", M: "MID", A: "ATT", F: "ATT",
      GOALKEEPER: "GK", PORTIERE: "GK", DEFENDER: "DEF", DIFENSORE: "DEF",
      MIDFIELDER: "MID", CENTROCAMPISTA: "MID", FORWARD: "ATT",
      STRIKER: "ATT", ATTACKER: "ATT", ATTACCANTE: "ATT"
    };
    return map[raw] || raw;
  };

  const getRoleInitial = (player) => {
    const normalized = normalizePosition(player.pos_short || player.position || "");
    return roleInitials[normalized]?.[lang] || "-";
  };

  const getRoleDotClass = (player) => {
    const normalized = normalizePosition(player.pos_short || player.position || "");
    const map = {
      GK: "bg-amber-400 text-black",
      DEF: "bg-sky-400 text-black",
      MID: "bg-emerald-400 text-black",
      ATT: "bg-rose-400 text-black"
    };
    return map[normalized] || "bg-white/20 text-white";
  };

  const formatFxAvg = (value) => {
    if (value === null || value === undefined) return "-";
    return Number(value).toFixed(2);
  };

  const formatTeamLine = (player) => {
    const club = player.club_name || "-";
    const nation = player.team_name || "-";
    return `${club}/${nation}`;
  };

  return (
    <section className="relative min-h-[60vh] flex items-center justify-center py-20 px-4 overflow-hidden">
      <TechBackground />
      <div className="max-w-6xl w-full mx-auto z-10">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid md:grid-cols-2 gap-8 md:gap-12 items-center"
        >
          {/* Text Content */}
          <div className="text-left">
            <p className="text-[#5DFDCB] font-display font-bold uppercase tracking-[0.3em] text-sm mb-4">
              {t.topPlayers.subtitle}
            </p>
            <h2 className="text-4xl md:text-6xl font-display font-black text-white uppercase mb-6">
              {t.topPlayers.title}
            </h2>
            <p className="text-white/70 font-sans text-lg leading-relaxed mb-8">
              {t.topPlayers.description}
            </p>
            <Link to="/top-players">
              <button className="bg-transparent border-2 border-[#5DFDCB] text-[#5DFDCB] px-8 py-3 rounded-full font-display font-bold uppercase tracking-wider text-sm hover:bg-[#5DFDCB] hover:text-black transition-all duration-300">
                {t.topPlayers.cta}
              </button>
            </Link>
          </div>

          {/* Players List */}
          <div className="bg-black/40 backdrop-blur-xl border border-[#5DFDCB]/20 rounded-2xl p-6">
            {isLoading && (
              <p className="text-white/60 font-sans text-center py-8">
                {lang === "it" ? "Caricamento..." : "Loading..."}
              </p>
            )}

            {!isLoading && players.length === 0 && (
              <div className="text-center py-8">
                <p className="text-white/60 font-sans text-lg">
                  {t.topPlayers.noPlayers}
                </p>
              </div>
            )}

            {!isLoading && players.length > 0 && (
              <div className="space-y-4">
                {players.map((player, index) => (
                  <div
                    key={`${player.athlete_shortname}-${index}`}
                    className="flex items-center gap-3 border-b border-white/10 pb-3 last:border-0"
                  >
                    <div className="flex items-center justify-center">
                      {player.picture ? (
                        <img
                          src={player.picture}
                          alt={player.athlete_shortname || "Player"}
                          className="w-10 h-10 rounded-full object-cover border border-[#5DFDCB]/30"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-white/10 border border-[#5DFDCB]/20" />
                      )}
                    </div>
                    <div className="flex items-center justify-center">
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-display font-bold ${getRoleDotClass(player)}`}
                      >
                        {getRoleInitial(player)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-display font-bold uppercase text-sm truncate">
                        {player.athlete_shortname || "-"}
                      </div>
                      <div className="text-white/50 font-sans text-xs truncate">
                        {formatTeamLine(player)}
                      </div>
                    </div>
                    <div className="text-white font-display font-bold text-right text-lg">
                      {formatFxAvg(player.av_fx_pts)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Player Costs Preview Component
const PlayerCostsPreview = ({ lang, t }) => {
  const [players, setPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadPlayerCosts = async () => {
      try {
        setIsLoading(true);
        const isLocalhost = window.location.hostname === "localhost";
        const apiUrl = isLocalhost
          ? "http://localhost:8000/api/current-event-prices"
          : "https://app.fanteex.com/api/current-event-prices";
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        const data = await response.json();
        const list = Array.isArray(data.players) ? data.players : [];
        if (isMounted) {
          const sorted = [...list].sort((a, b) => {
            const costA = Number(a.event_unit_cost ?? -Infinity);
            const costB = Number(b.event_unit_cost ?? -Infinity);
            return costB - costA;
          });
          setPlayers(sorted.slice(0, 5));
        }
      } catch (error) {
        if (isMounted) {
          setPlayers([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadPlayerCosts();

    return () => {
      isMounted = false;
    };
  }, []);

  const roleInitials = {
    GK: { it: "P", en: "G" },
    DEF: { it: "D", en: "D" },
    MID: { it: "C", en: "M" },
    ATT: { it: "A", en: "A" }
  };

  const normalizePosition = (value) => {
    if (!value) return "";
    const raw = String(value).trim().toUpperCase();
    const map = {
      G: "GK", D: "DEF", M: "MID", A: "ATT", F: "ATT",
      GOALKEEPER: "GK", PORTIERE: "GK", DEFENDER: "DEF", DIFENSORE: "DEF",
      MIDFIELDER: "MID", CENTROCAMPISTA: "MID", FORWARD: "ATT",
      STRIKER: "ATT", ATTACKER: "ATT", ATTACCANTE: "ATT"
    };
    return map[raw] || raw;
  };

  const getRoleInitial = (player) => {
    const normalized = normalizePosition(player.pos_short || player.position || "");
    return roleInitials[normalized]?.[lang] || "-";
  };

  const getRoleDotClass = (player) => {
    const normalized = normalizePosition(player.pos_short || player.position || "");
    const map = {
      GK: "bg-amber-400 text-black",
      DEF: "bg-sky-400 text-black",
      MID: "bg-emerald-400 text-black",
      ATT: "bg-rose-400 text-black"
    };
    return map[normalized] || "bg-white/20 text-white";
  };

  const formatCost = (cost) => {
    if (cost === null || cost === undefined) return "-";
    return Number(cost).toFixed(1);
  };

  return (
    <section className="relative min-h-[60vh] flex items-center justify-center py-20 px-4 overflow-hidden">
      <TechBackground />
      <div className="max-w-6xl w-full mx-auto z-10">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid md:grid-cols-2 gap-8 md:gap-12 items-center"
        >
          {/* Players List */}
          <div className="bg-black/40 backdrop-blur-xl border border-[#5DFDCB]/20 rounded-2xl p-6 order-2 md:order-1">
            {isLoading && (
              <p className="text-white/60 font-sans text-center py-8">
                {lang === "it" ? "Caricamento..." : "Loading..."}
              </p>
            )}

            {!isLoading && players.length === 0 && (
              <div className="text-center py-8">
                <p className="text-white/60 font-sans text-lg">
                  {t.playerCosts.noPlayers}
                </p>
              </div>
            )}

            {!isLoading && players.length > 0 && (
              <div className="space-y-4">
                {players.map((player, index) => (
                  <div
                    key={`${player.athlete_shortname}-${index}`}
                    className="flex items-center gap-3 border-b border-white/10 pb-3 last:border-0"
                  >
                    <div className="flex items-center justify-center">
                      {player.picture ? (
                        <img
                          src={player.picture}
                          alt={player.athlete_shortname || "Player"}
                          className="w-10 h-10 rounded-full object-cover border border-[#5DFDCB]/30"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-white/10 border border-[#5DFDCB]/20" />
                      )}
                    </div>
                    <div className="flex items-center justify-center">
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-display font-bold ${getRoleDotClass(player)}`}
                      >
                        {getRoleInitial(player)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-display font-bold uppercase text-sm truncate">
                        {player.athlete_shortname || "-"}
                      </div>
                      <div className="text-white/50 font-sans text-xs truncate">
                        {player.team_name || "-"}
                      </div>
                    </div>
                    <div className="text-white font-display font-bold text-right text-lg">
                      {formatCost(player.event_unit_cost)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Text Content */}
          <div className="text-left order-1 md:order-2">
            <p className="text-[#5DFDCB] font-display font-bold uppercase tracking-[0.3em] text-sm mb-4">
              {t.playerCosts.subtitle}
            </p>
            <h2 className="text-4xl md:text-6xl font-display font-black text-white uppercase mb-6">
              {t.playerCosts.title}
            </h2>
            <p className="text-white/70 font-sans text-lg leading-relaxed mb-8">
              {t.playerCosts.description}
            </p>
            <Link to="/players-costs">
              <button className="bg-transparent border-2 border-[#5DFDCB] text-[#5DFDCB] px-8 py-3 rounded-full font-display font-bold uppercase tracking-wider text-sm hover:bg-[#5DFDCB] hover:text-black transition-all duration-300">
                {t.playerCosts.cta}
              </button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Home = () => {
  const [lang, setLang] = useState('it'); // Default Italian
  const [menuOpen, setMenuOpen] = useState(false);
  const [formButtonReady, setFormButtonReady] = useState(false);
  const formButtonInitRef = useRef(false);
  const t = content[lang];

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 200]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  const toggleLanguage = () => {
    setLang(prev => prev === 'it' ? 'en' : 'it');
  };

  const toggleMenu = () => {
    setMenuOpen(prev => !prev);
  };

  const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const initFormButton = () => {
      if (formButtonInitRef.current) {
          setFormButtonReady(true);
          return;
      }

      window.formbutton = window.formbutton || function () {
          (window.formbutton.q = window.formbutton.q || []).push(arguments);
      };

      window.formbutton("create", {
          action: "https://formspree.io/f/mqeeeapb",
          title: "Scrivici",
          fields: [
              {
                  type: "email",
                  label: "Email",
                  name: "email",
                  required: true,
                  placeholder: "La tua email",
              },
              {
                  type: "textarea",
                  label: "Messaggio",
                  name: "message",
                  placeholder: "Raccontaci cosa ti interessa: giocare, info, partnership, altro...",
              },
              { type: "submit" },
          ],
          styles: {
              title: {
                  backgroundColor: "rgb(93, 253, 203)",
              },
              button: {
                  backgroundColor: "rgb(93, 253, 203)",
              },
          },
      });

      formButtonInitRef.current = true;
      setFormButtonReady(true);
  };

  const openFormbutton = () => {
      if (!formButtonInitRef.current && !document.getElementById("formspree-formbutton-script")) {
          const script = document.createElement("script");
          script.id = "formspree-formbutton-script";
          script.src = "https://formspree.io/js/formbutton-v1.min.js";
          script.async = true;
          script.onload = () => {
              initFormButton();
              setTimeout(() => {
                  const formButton = document.querySelector(".formbutton");
                  if (formButton) {
                      formButton.click();
                  }
              }, 50);
          };
          document.body.appendChild(script);
          return;
      }

      initFormButton();
      const formButton = document.querySelector(".formbutton");
      if (formButton) {
          formButton.click();
      }
  };

  useEffect(() => {
      const schedulePreload = () => {
          if ("requestIdleCallback" in window) {
              window.requestIdleCallback(() => {
                  if (!document.getElementById("formspree-formbutton-script")) {
                      const script = document.createElement("script");
                      script.id = "formspree-formbutton-script";
                      script.src = "https://formspree.io/js/formbutton-v1.min.js";
                      script.async = true;
                      script.onload = initFormButton;
                      document.body.appendChild(script);
                  }
              }, { timeout: 4000 });
          }
      };

      window.addEventListener("load", schedulePreload, { once: true });
      return () => window.removeEventListener("load", schedulePreload);
  }, []);

  const menuItems = [
    { label: t.nav.menu.rules, path: "/rules" },
    { label: t.nav.menu.community, path: "/community" },
    { label: t.nav.menu.topPlayers, path: "/top-players" },
    { label: t.nav.menu.playerValues, path: "/players-costs" },
    { label: t.nav.menu.download, path: "/download" },
    { label: t.nav.menu.contact, path: "/#contact" }
  ];

  return (
    <div className="relative min-h-screen bg-fanteex-dark overflow-x-hidden flex flex-col font-sans selection:bg-[#5DFDCB] selection:text-fanteex-dark">
      
      {/* Background - NO SOCCER FIELD */}
      <div className="fixed inset-0 z-0 pointer-events-none bg-[#0f0518]">
         {/* Noise Texture */}
         <div className="absolute inset-0 opacity-15 mix-blend-overlay" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/noise.svg)` }}></div>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 px-6 py-6 transition-all duration-300 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
            {/* Left: Hamburger Menu + Logo */}
            <div className="flex items-center gap-4">
                {/* Hamburger Menu Button */}
                <button
                    onClick={toggleMenu}
                    className="text-white/70 hover:text-[#5DFDCB] transition-colors p-2"
                    aria-label="Menu"
                >
                    {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>

                {/* Clickable Logo to Scroll Top */}
                <div className="flex items-center gap-3 cursor-pointer group" onClick={scrollToTop}>
                   <img src={LOGO_SMALL} alt="FANTEEX" className="h-8 md:h-10 w-auto group-hover:drop-shadow-[0_0_8px_#5DFDCB] transition-all" />
                </div>
            </div>

            {/* Right: Sign In / Register buttons */}
            <div className="flex items-center gap-3 md:gap-4">
                <a href="https://app.fanteex.com/signin.html">
                    <button className="bg-transparent border border-white/30 text-white px-4 md:px-6 py-2 rounded-full font-display font-bold uppercase text-xs md:text-sm tracking-wider hover:border-[#5DFDCB] hover:text-[#5DFDCB] transition-all duration-300">
                        {t.nav.signIn}
                    </button>
                </a>
                <a href="https://app.fanteex.com/register.html" className="hidden md:block">
                    <button className="bg-[#5DFDCB] text-black px-4 md:px-6 py-2 rounded-full font-display font-black uppercase text-xs md:text-sm tracking-wider hover:shadow-[0_0_20px_#5DFDCB] transition-all duration-300">
                        {t.nav.register}
                    </button>
                </a>
            </div>
        </div>
      </nav>

      {/* Slide-out Menu */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleMenu}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed top-0 left-0 h-full w-72 md:w-80 bg-[#0f0518]/95 backdrop-blur-xl border-r border-[#5DFDCB]/20 z-50 flex flex-col"
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <img src={LOGO_SMALL} alt="FANTEEX" className="h-8 w-auto" />
                <button onClick={toggleMenu} className="text-white/70 hover:text-[#5DFDCB] transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Menu Items */}
              <div className="flex-1 py-6">
                {menuItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    onClick={toggleMenu}
                    className="block px-6 py-4 text-white/80 hover:text-[#5DFDCB] hover:bg-white/5 font-display font-bold uppercase tracking-wider text-sm transition-all border-l-2 border-transparent hover:border-[#5DFDCB]"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              {/* Language Switch at bottom */}
              <div className="p-6 border-t border-white/10">
                <button
                    onClick={toggleLanguage}
                    className="flex items-center gap-2 text-white/70 hover:text-[#5DFDCB] transition-colors font-display font-bold uppercase text-sm tracking-wider"
                >
                    <Globe className="w-4 h-4" />
                    <span>{lang === 'it' ? 'English' : 'Italiano'}</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* MAIN CONTENT */}
      <main>
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 px-4 overflow-hidden">
        <TechBackground />

        <motion.div 
            style={{ y: heroY, opacity: heroOpacity }}
            className="w-full max-w-7xl mx-auto flex flex-col items-center justify-center text-center z-10"
        >
            {/* V4 Monogram - Transparent & Glowing */}
            <motion.div 
                initial={{ scale: 0.8, opacity: 0, rotate: -10 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="mb-8 relative"
            >
                <div className="absolute inset-0 bg-[#5DFDCB]/20 blur-[80px] rounded-full animate-pulse-glow" />
                <img 
                    src={MONOGRAM} 
                    alt="Fanteex Icon" 
                    className="w-40 h-40 md:w-56 md:h-56 object-contain relative z-10 animate-float drop-shadow-[0_0_25px_rgba(93,253,203,0.4)]"
                />
            </motion.div>

            {/* Main Title - 2 LINES, NO WRAP, RESPONSIVE SIZE */}
            <h1 className="flex flex-col items-center leading-[0.85] uppercase text-white drop-shadow-2xl relative w-full">
                
                {/* Line 1 */}
                <span className="font-display font-black text-[clamp(3.5rem,10vw,11rem)] tracking-tighter mix-blend-difference relative z-20 whitespace-nowrap">
                    {t.hero.titlePart1}
                </span>
                
                {/* Line 2 */}
                <span className="font-display font-black text-[clamp(3.5rem,10vw,11rem)] text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-500 tracking-tighter relative z-10 whitespace-nowrap">
                    {t.hero.titlePart2}
                </span>
            </h1>

            {/* Subtitle */}
            <motion.p 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-10 font-sans text-[#5DFDCB] text-xl md:text-2xl uppercase tracking-[0.3em] font-bold text-glow"
            >
                {t.hero.subtitle}
            </motion.p>
            
            {/* App Download Badges */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="mt-10 flex flex-col items-center gap-4"
            >
                <p className="text-white/30 text-xs font-display uppercase tracking-[0.3em]">
                    {lang === 'it' ? 'Disponibile gratuitamente su' : 'Available for free on'}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 items-center">
                    <a href="https://apps.apple.com/it/app/fanteex-football/id6767338512" target="_blank" rel="noopener noreferrer" className="group">
                        <img
                            src={lang === 'it' ? '/assets/app/badge-ios-it.svg' : '/assets/app/badge-ios-en.svg'}
                            alt="Download on the App Store"
                            className="h-11 md:h-13 w-auto group-hover:scale-105 group-hover:drop-shadow-[0_0_15px_rgba(93,253,203,0.4)] transition-all duration-300"
                        />
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=com.fanteex.app&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="group">
                        <img
                            src={lang === 'it' ? '/assets/app/badge-android-it.png' : '/assets/app/badge-android-en.png'}
                            alt="Get it on Google Play"
                            className="h-11 md:h-13 w-auto group-hover:scale-105 group-hover:drop-shadow-[0_0_15px_rgba(93,253,203,0.4)] transition-all duration-300"
                        />
                    </a>
                </div>
            </motion.div>

            {/* Scroll Indicator */}
            <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute bottom-10 opacity-50"
            >
                <ChevronDown className="w-10 h-10 text-white" />
            </motion.div>
        </motion.div>
      </section>

      {/* SECTIONS */}
      <FeatureSection 
         title={t.features.section1.title}
         subtitle={t.features.section1.subtitle}
         description={t.features.section1.description}
         align="left"
         screenshots={[SCREEN_MATCH, SCREEN_DETAILS]}
      />

      <FeatureSection
         title={t.features.section2.title}
         subtitle={t.features.section2.subtitle}
         description={t.features.section2.description}
         align="right"
         screenshots={[SCREEN_LEADERBOARD, SCREEN_TEAMS]}
      />

      {/* RULES PREVIEW SECTION */}
      <section className="relative min-h-[60vh] flex items-center justify-center py-20 px-4 overflow-hidden">
        <TechBackground />
        <div className="max-w-6xl w-full mx-auto z-10">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="grid md:grid-cols-2 gap-8 md:gap-12 items-center"
          >
            {/* Text Content */}
            <div className="text-left">
              <p className="text-[#5DFDCB] font-display font-bold uppercase tracking-[0.3em] text-sm mb-4">
                {t.rules.subtitle}
              </p>
              <h2 className="text-4xl md:text-6xl font-display font-black text-white uppercase mb-6">
                {t.rules.title}
              </h2>
              <p className="text-white/70 font-sans text-lg leading-relaxed mb-8">
                {t.rules.description}
              </p>
              <Link to="/rules">
                <button className="bg-transparent border-2 border-[#5DFDCB] text-[#5DFDCB] px-8 py-3 rounded-full font-display font-bold uppercase tracking-wider text-sm hover:bg-[#5DFDCB] hover:text-black transition-all duration-300">
                  {t.rules.cta}
                </button>
              </Link>
            </div>

            {/* Rules Visual - Key Points */}
            <div className="bg-black/40 backdrop-blur-xl border border-[#5DFDCB]/20 rounded-2xl p-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#5DFDCB]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#5DFDCB] font-display font-black text-xl">500</span>
                  </div>
                  <div>
                    <p className="text-white font-display font-bold uppercase">TEEX {lang === 'it' ? 'Iniziali' : 'Starting'}</p>
                    <p className="text-white/60 font-sans text-sm">{lang === 'it' ? 'Inizia con 500 TEEX' : 'Start with 500 TEEX'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#5DFDCB]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#5DFDCB] font-display font-black text-xl">10</span>
                  </div>
                  <div>
                    <p className="text-white font-display font-bold uppercase">Budget {lang === 'it' ? 'Rosa' : 'Squad'}</p>
                    <p className="text-white/60 font-sans text-sm">{lang === 'it' ? 'Max 10 TEEX per sfida' : 'Max 10 TEEX per challenge'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#5DFDCB]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#5DFDCB] font-display font-black text-xl">+3</span>
                  </div>
                  <div>
                    <p className="text-white font-display font-bold uppercase">{lang === 'it' ? 'Punti Gol' : 'Goal Points'}</p>
                    <p className="text-white/60 font-sans text-sm">{lang === 'it' ? '+3 punti per ogni gol segnato' : '+3 points for each goal scored'}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* COMMUNITY PREVIEW SECTION */}
      <section className="relative min-h-[60vh] flex items-center justify-center py-20 px-4 overflow-hidden">
        <TechBackground />
        <div className="max-w-6xl w-full mx-auto z-10">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="grid md:grid-cols-2 gap-8 md:gap-12 items-center"
          >
            {/* Video */}
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-black/60 border border-[#5DFDCB]/20 order-2 md:order-1">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="none"
                className="absolute inset-0 w-full h-full object-cover"
              >
                <source src="/assets/francesco.mp4" type="video/mp4" />
              </video>
              {/* Playing indicator */}
              <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/50 px-2 py-1 rounded-full">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-white/70 text-xs font-sans uppercase">Live</span>
              </div>
            </div>

            {/* Text Content */}
            <div className="text-left order-1 md:order-2">
              <p className="text-[#5DFDCB] font-display font-bold uppercase tracking-[0.3em] text-sm mb-4">
                {t.community.subtitle}
              </p>
              <h2 className="text-4xl md:text-6xl font-display font-black text-white uppercase mb-6">
                {t.community.title}
              </h2>
              <p className="text-white/70 font-sans text-lg leading-relaxed mb-8">
                {t.community.description}
              </p>
              <Link to="/community">
                <button className="bg-transparent border-2 border-[#5DFDCB] text-[#5DFDCB] px-8 py-3 rounded-full font-display font-bold uppercase tracking-wider text-sm hover:bg-[#5DFDCB] hover:text-black transition-all duration-300">
                  {t.community.cta}
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TOP PLAYERS PREVIEW SECTION */}
      <TopPlayersPreview lang={lang} t={t} />

      {/* PLAYER COSTS PREVIEW SECTION */}
      <PlayerCostsPreview lang={lang} t={t} />

      {/* PUBLISHER CONTENT SECTION */}
      <section className="relative py-20 px-4 overflow-hidden">
        <TechBackground />
        <div className="max-w-5xl w-full mx-auto z-10 relative">
          <div className="bg-black/60 backdrop-blur-xl border border-[#5DFDCB]/20 rounded-2xl p-8 md:p-12">
            <p className="text-[#5DFDCB] font-display font-bold uppercase tracking-[0.3em] text-sm mb-4">
              {t.publisherContent.subtitle}
            </p>
            <h2 className="text-3xl md:text-5xl font-display font-black text-white uppercase mb-8">
              {t.publisherContent.title}
            </h2>

            <div className="space-y-5 mb-10">
              {t.publisherContent.paragraphs.map((paragraph, index) => (
                <p key={index} className="text-white/80 font-sans text-base md:text-lg leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>

            <h3 className="text-2xl md:text-3xl font-display font-black text-[#5DFDCB] uppercase mb-6">
              {t.publisherContent.faqTitle}
            </h3>
            <div className="space-y-6">
              {t.publisherContent.faqs.map((faq, index) => (
                <article key={index} className="border border-white/10 rounded-xl p-5 bg-white/[0.02]">
                  <h4 className="text-white font-display font-bold uppercase text-lg mb-2">
                    {faq.question}
                  </h4>
                  <p className="text-white/75 font-sans leading-relaxed">
                    {faq.answer}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA / WAITLIST - WITH LASER BORDER */}
      <section id="contact" className="relative min-h-[80vh] flex items-center justify-center py-20 px-4 overflow-hidden">
        <TechBackground />
        
        <div className="max-w-4xl w-full text-center z-10 relative group">
            
            {/* LASER BORDER CONTAINER */}
            <div className="relative rounded-[2rem] overflow-hidden p-[2px]">
                 {/* Top Laser */}
                <div className="absolute top-0 left-0 w-full h-[4px] bg-gradient-to-r from-transparent via-[#5DFDCB] to-transparent -translate-x-full animate-[laser-x_3s_linear_infinite] z-30 shadow-[0_0_20px_#5DFDCB]" />
                {/* Right Laser */}
                <div className="absolute top-0 right-0 w-[4px] h-full bg-gradient-to-b from-transparent via-[#5DFDCB] to-transparent -translate-y-full animate-[laser-y_3s_linear_infinite_0.75s] z-30 shadow-[0_0_20px_#5DFDCB]" />
                {/* Bottom Laser */}
                <div className="absolute bottom-0 right-0 w-full h-[4px] bg-gradient-to-l from-transparent via-[#5DFDCB] to-transparent translate-x-full animate-[laser-x_3s_linear_infinite_1.5s] z-30 shadow-[0_0_20px_#5DFDCB]" />
                {/* Left Laser */}
                <div className="absolute bottom-0 left-0 w-[4px] h-full bg-gradient-to-t from-transparent via-[#5DFDCB] to-transparent translate-y-full animate-[laser-y_3s_linear_infinite_2.25s] z-30 shadow-[0_0_20px_#5DFDCB]" />

                {/* Inner Content */}
                <div className="bg-black/80 backdrop-blur-2xl border border-[#5DFDCB]/10 p-8 md:p-16 rounded-[2rem] relative overflow-hidden">
                    {/* Hover Shine Effect */}
                     <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] pointer-events-none" />

                    <h2 className="text-5xl md:text-7xl font-display font-black text-white uppercase mb-6">
                        {t.cta.title}
                    </h2>
                    <p className="font-sans text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
                        {t.cta.description}
                    </p>
                    
                    <div className="flex flex-col items-center w-full">
                        <button
                          type="button"
                          onClick={openFormbutton}
                          className="bg-fanteex-mint hover:bg-fanteex-mintHover text-fanteex-dark font-display font-black text-lg uppercase tracking-widest px-10 h-12 rounded-lg transition-all shadow-[0_0_15px_rgba(0,255,163,0.3)] hover:shadow-[0_0_25px_rgba(0,255,163,0.5)]"
                        >
                          {formButtonReady ? t.cta.form.buttonText : `${t.cta.form.buttonText}...`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
      </section>

      </main>

      {/* Minimal Footer */}
      <footer className="w-full py-12 border-t border-white/5 bg-black text-center relative z-20">
        <img src={LOGO_SMALL} alt="Fanteex" className="h-8 w-auto mx-auto mb-6 opacity-50 grayscale hover:grayscale-0 transition-all" />
        <p className="text-fanteex-textMuted/30 text-xs font-sans uppercase tracking-widest">
            &copy; 2026 Fanteex. {t.footer.rights}
        </p>
      </footer>

      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
};

// SubPage Component Template
const SubPage = ({ titleKey }) => {
  const [lang, setLang] = useState('it');
  const [menuOpen, setMenuOpen] = useState(false);
  const t = content[lang];

  const toggleLanguage = () => {
    setLang(prev => prev === 'it' ? 'en' : 'it');
  };

  const toggleMenu = () => {
    setMenuOpen(prev => !prev);
  };

  const menuItems = [
    { label: t.nav.menu.rules, path: "/rules" },
    { label: t.nav.menu.community, path: "/community" },
    { label: t.nav.menu.topPlayers, path: "/top-players" },
    { label: t.nav.menu.playerValues, path: "/players-costs" },
    { label: t.nav.menu.download, path: "/download" },
    { label: t.nav.menu.contact, path: "/#contact" }
  ];

  const pageTitles = {
    rules: { it: "Regolamento", en: "Game Rules" },
    community: { it: "Community", en: "Community" },
    topPlayers: { it: "Top Players", en: "Top Players" },
    playerValues: { it: "Prezzi Giocatori", en: "Player Values" },
    contacts: { it: "Contatti", en: "Get in Touch" }
  };

  const pageTitle = pageTitles[titleKey]?.[lang] || titleKey;

  return (
    <div className="relative min-h-screen bg-fanteex-dark overflow-x-hidden flex flex-col font-sans selection:bg-[#5DFDCB] selection:text-fanteex-dark">
      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none bg-[#0f0518]">
        <div className="absolute inset-0 opacity-15 mix-blend-overlay" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/noise.svg)` }}></div>
      </div>

      <TechBackground />

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 px-6 py-6 transition-all duration-300 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* Left: Hamburger Menu + Logo */}
          <div className="flex items-center gap-4">
            <button
              onClick={toggleMenu}
              className="text-white/70 hover:text-[#5DFDCB] transition-colors p-2"
              aria-label="Menu"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <Link to="/" className="flex items-center gap-3 cursor-pointer group">
              <img src={LOGO_SMALL} alt="FANTEEX" className="h-8 md:h-10 w-auto group-hover:drop-shadow-[0_0_8px_#5DFDCB] transition-all" />
            </Link>
          </div>

          {/* Right: Sign In / Register buttons */}
          <div className="flex items-center gap-3 md:gap-4">
            <a href="https://app.fanteex.com/signin.html">
              <button className="bg-transparent border border-white/30 text-white px-4 md:px-6 py-2 rounded-full font-display font-bold uppercase text-xs md:text-sm tracking-wider hover:border-[#5DFDCB] hover:text-[#5DFDCB] transition-all duration-300">
                {t.nav.signIn}
              </button>
            </a>
            <a href="https://app.fanteex.com/register.html" className="hidden md:block">
              <button className="bg-[#5DFDCB] text-black px-4 md:px-6 py-2 rounded-full font-display font-black uppercase text-xs md:text-sm tracking-wider hover:shadow-[0_0_20px_#5DFDCB] transition-all duration-300">
                {t.nav.register}
              </button>
            </a>
          </div>
        </div>
      </nav>

      {/* Slide-out Menu */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleMenu}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />

            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed top-0 left-0 h-full w-72 md:w-80 bg-[#0f0518]/95 backdrop-blur-xl border-r border-[#5DFDCB]/20 z-50 flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <img src={LOGO_SMALL} alt="FANTEEX" className="h-8 w-auto" />
                <button onClick={toggleMenu} className="text-white/70 hover:text-[#5DFDCB] transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 py-6">
                {menuItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    onClick={toggleMenu}
                    className="block px-6 py-4 text-white/80 hover:text-[#5DFDCB] hover:bg-white/5 font-display font-bold uppercase tracking-wider text-sm transition-all border-l-2 border-transparent hover:border-[#5DFDCB]"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              <div className="p-6 border-t border-white/10">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-2 text-white/70 hover:text-[#5DFDCB] transition-colors font-display font-bold uppercase text-sm tracking-wider"
                >
                  <Globe className="w-4 h-4" />
                  <span>{lang === 'it' ? 'English' : 'Italiano'}</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Page Content */}
      <main className="flex-1 flex items-center justify-center pt-32 pb-20 px-4 relative z-10">
        <div className="max-w-4xl w-full text-center">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-5xl md:text-7xl font-display font-black text-white uppercase mb-8"
          >
            {pageTitle}
          </motion.h1>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-black/40 backdrop-blur-xl border border-[#5DFDCB]/20 rounded-2xl p-8 md:p-12"
          >
            <p className="text-white/60 text-lg font-sans">
              {lang === 'it' ? 'Contenuto in arrivo...' : 'Content coming soon...'}
            </p>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full py-12 border-t border-white/5 bg-black text-center relative z-20">
        <Link to="/">
          <img src={LOGO_SMALL} alt="Fanteex" className="h-8 w-auto mx-auto mb-6 opacity-50 grayscale hover:grayscale-0 transition-all" />
        </Link>
        <p className="text-fanteex-textMuted/30 text-xs font-sans uppercase tracking-widest">
          &copy; 2026 Fanteex. {t.footer.rights}
        </p>
      </footer>

      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToHash />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/rules" element={<RulesPage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/top-players" element={<TopPlayersPage />} />
        <Route path="/players-costs" element={<PlayerCostsPage />} />
        <Route path="/player/:athleteId" element={<PlayerProfilePage />} />
        <Route path="/player/:athleteId/:athleteSlug" element={<PlayerProfilePage />} />
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/contacts" element={<SubPage titleKey="contacts" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
